import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useBetSlip, RunnerCard } from '../context/BetSlipContext';

// Owner 2026-10-13: one card per horse clicked on Bucket Next Up. Win / Place back and Win / Place lay
// prices with a stake box each; a back stake hides the lay rows and vice versa. "Add in multi" adds the
// horse as a Win / Place / Each-way leg to the multi built at the bottom of the slip.
const key = (v: string) => String(v || '').toUpperCase().replace(/^\d+[.]?\s*/, '').replace(/[^A-Z0-9]/g, '');
type BR = { selection_id: number; runner_name: string; best_back?: number | null; best_lay?: number | null; status?: string };
type BM = { market_id: string; market_name?: string; status?: string; runners?: BR[] } | null;
const r2 = (n: number) => Math.round(n * 100) / 100;
const ROWS: { type: 'WIN' | 'PLACE'; side: 'BACK' | 'LAY'; label: string }[] = [
  { type: 'WIN', side: 'BACK', label: 'Win' }, { type: 'PLACE', side: 'BACK', label: 'Place' },
  { type: 'WIN', side: 'LAY', label: 'Lay win' }, { type: 'PLACE', side: 'LAY', label: 'Lay place' },
];

const SlipRunnerCard: React.FC<{ card: RunnerCard }> = ({ card }) => {
  const { items, addItem, removeItem, updateItem, addMultiple, multipleItems, removeCard } = useBetSlip();
  const [win, setWin] = useState<BM>(null);
  const [place, setPlace] = useState<BM>(null);
  const [meeting, setMeeting] = useState(card.track);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let on = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API_BASE_URL}/api/races/${encodeURIComponent(card.track)}/${card.race}`, { params: { date: card.date }, timeout: 20000 });
        if (!on) return;
        setWin(r.data.betfairMarket || null); setPlace(r.data.betfairPlaceMarket || null);
        if (r.data.race?.meeting_name) setMeeting(r.data.race.meeting_name);
        setErr('');
      } catch (e: any) { if (on) setErr(e?.response?.data?.error || 'Could not load Betfair prices.'); }
      finally { if (on) setLoading(false); }
    };
    void load();
    const t = window.setInterval(load, 15000);
    return () => { on = false; window.clearInterval(t); };
  }, [card.track, card.race, card.date]);

  const wr = win?.runners?.find(x => key(x.runner_name) === key(card.runner));
  const pr = wr ? place?.runners?.find(x => Number(x.selection_id) === Number(wr.selection_id)) : undefined;
  const num = Number(String(wr?.runner_name || '').match(/^(\d+)/)?.[1]) || undefined;
  const winOpen = !!win && win.status === 'OPEN' && !!wr && wr.status !== 'REMOVED';
  const placeOpen = !!place && place.status === 'OPEN' && !!pr && pr.status !== 'REMOVED';
  const mine = items.filter(i => i.cardKey === card.key);
  const backOn = mine.some(i => i.side === 'BACK' && i.stake > 0), layOn = mine.some(i => i.side === 'LAY' && i.stake > 0);
  const rows = ROWS.filter(r => (backOn ? r.side === 'BACK' : layOn ? r.side === 'LAY' : true));

  const setStake = (type: 'WIN' | 'PLACE', side: 'BACK' | 'LAY', raw: string) => {
    const m = type === 'WIN' ? win : place, r = type === 'WIN' ? wr : pr;
    if (!m || !r) return;
    const v = r2(Number(raw)), price = Number(side === 'BACK' ? r.best_back : r.best_lay);
    const ex = mine.find(i => i.marketId === m.market_id && i.side === side);
    if (!(v > 0)) { if (ex) removeItem(ex.id); return; }
    // Backing and laying the same horse makes no sense: a stake on one side clears the other.
    mine.filter(i => i.side !== side).forEach(i => removeItem(i.id));
    if (ex) { updateItem(ex.id, { stake: v, ...(price > 1 ? { price } : {}) }); return; }
    addItem({ marketId: m.market_id, selectionId: Number(r.selection_id), runnerName: card.runner, side, price: price > 1 ? price : 0,
      stake: v, marketType: type, marketName: type === 'PLACE' ? 'PLACE' : (win?.market_name || 'WIN'),
      meetingName: meeting, raceNumber: card.race, date: card.date, number: num, cardKey: card.key, racingCode: 'HORSE' });
  };
  const leg = (type: 'WIN' | 'PLACE' | 'EW') => {
    const on = !!wr && !!win && multipleItems.some(x => x.win_market_id === win.market_id && Number(x.selection_id) === Number(wr.selection_id) && (x.side || 'BACK') === 'BACK' && x.type === type);
    return <button type="button" key={type} aria-pressed={on} className={on ? 'srcard-leg on' : 'srcard-leg'}
      disabled={!winOpen || (type !== 'WIN' && !placeOpen)}
      onClick={() => wr && win && addMultiple({ win_market_id: win.market_id, selection_id: Number(wr.selection_id), runner: card.runner, number: num,
        track: meeting, race: card.race, type, side: 'BACK', racing_code: 'HORSE',
        prices: { ...(Number(wr.best_back) > 1 ? { WIN: Number(wr.best_back) } : {}), ...(Number(pr?.best_back) > 1 ? { PLACE: Number(pr!.best_back) } : {}) } })}>
      {type === 'WIN' ? 'Win' : type === 'PLACE' ? 'Place' : 'EW'}
    </button>;
  };

  return <div className="srcard" role="group" aria-label={`Bets on ${card.runner}`}>
    <div className="srcard-head">
      <div><strong>{num ? num + '. ' : ''}{card.runner.toUpperCase()}</strong>
        <span className="srcard-race">{meeting} – Race {card.race}</span></div>
      <button type="button" className="slip-remove" aria-label={'Remove ' + card.runner} onClick={() => removeCard(card.key)}>×</button>
    </div>
    {loading && <p className="srcard-note" role="status">Loading Betfair prices…</p>}
    {err && <p className="slip-warn" role="alert">{err}</p>}
    {!loading && !err && !wr && <p className="srcard-note">Not found in the Betfair win market for this race.</p>}
    {!loading && wr && !winOpen && <p className="srcard-note">Betting closed ({wr.status === 'REMOVED' ? 'scratched' : String(win?.status || 'no market').toLowerCase()}).</p>}
    {wr && rows.map((r, i) => {
      const m = r.type === 'WIN' ? win : place, x = r.type === 'WIN' ? wr : pr, ok = r.type === 'WIN' ? winOpen : placeOpen;
      const price = Number(r.side === 'BACK' ? x?.best_back : x?.best_lay);
      const ex = m ? mine.find(it => it.marketId === m.market_id && it.side === r.side) : undefined;
      const stake = ex ? ex.stake : 0;
      return <div className={'srcard-row ' + (r.side === 'LAY' ? 'lay' : 'back')} key={r.type + r.side}>
        <span className="srcard-label">{r.label}</span>
        <span className="srcard-price">{price > 1 ? price.toFixed(2) : '—'}</span>
        <label className="srcard-stake"><span>$</span>
          <input type="number" min="0" step="1" inputMode="decimal" aria-label={`${r.label} stake for ${card.runner}`}
            autoFocus={i === 0 && !mine.length} disabled={!ok || !(price > 1)}
            value={stake > 0 ? stake : ''} onChange={e => setStake(r.type, r.side, e.target.value)} /></label>
        {stake > 0 && <small className="srcard-ret">{r.side === 'LAY' ? 'Liability' : 'Returns'} ${(r.side === 'LAY' ? stake * (price - 1) : stake * price).toFixed(2)}</small>}
      </div>;
    })}
    {wr && <div className="srcard-multi"><span>Add in multi</span>{leg('WIN')}{leg('PLACE')}{leg('EW')}</div>}
  </div>;
};
export default SlipRunnerCard;
